'use client';

import { useSwap } from 'src/hooks/velocityActions/swaps/useSwap';
import { calcSwapAmountAtMaxSlippage, getSwapToastId } from 'src/utils/swap';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import React, { useState } from 'react';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import useModal from 'src/components/Modals/hooks/useModal';
import TradeConfirmationModalWrapper from 'src/components/TradeConfirmationModal/TradeConfirmationModalWrapper';
import { TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME } from 'src/components/TradeConfirmationModal/consts';
import ValueDisplay from 'src/components/ValueDisplay';

const SwapConfirmationModal = () => {
	const swapDetails = useVelocityStore((s) => s.swap.swapDetails);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const { swap } = useSwap();
	const { hide } = useModal();

	const [isLoading, setIsLoading] = useState(false);

	if (!swapDetails) {
		return null;
	}

	const fromSpotConfig =
		spotMarketsSortedByMarketIndex[swapDetails.fromMarketIndex];
	const toSpotConfig =
		spotMarketsSortedByMarketIndex[swapDetails.toMarketIndex];
	const isExactIn = swapDetails.swapMode === 'ExactIn';
	const precision = isExactIn
		? fromSpotConfig.precision
		: toSpotConfig.precision;
	const precisionExp = isExactIn
		? fromSpotConfig.precisionExp
		: toSpotConfig.precisionExp;

	const exactAmount = isExactIn
		? swapDetails.quote.inAmount
		: swapDetails.quote.outAmount;

	const exactAmountString = (
		parseFloat(exactAmount) / precision.toNumber()
	).toFixed(precisionExp.toNumber());

	const slippage = +swapDetails.quote?.slippageBps / 100 || 0;
	const slippageString =
		slippage === 0
			? '0'
			: slippage > 0.01
				? `${slippage.toFixed(2)}%`
				: '< 0.1%';

	const worstAmount = calcSwapAmountAtMaxSlippage(
		isExactIn,
		swapDetails.quote,
		slippage as number,
		fromSpotConfig,
		toSpotConfig
	);

	const priceImpact = +(swapDetails.quote?.priceImpactPct ?? '0') || 0;
	const priceImpactString =
		priceImpact === 0
			? '0'
			: priceImpact > 0.001
				? `${(priceImpact * 100).toFixed(2)}%`
				: '< 0.1%';

	const confirmSwap = async () => {
		if (isLoading) {
			return;
		}

		setIsLoading(true);

		const toastId = getSwapToastId(
			swapDetails.fromMarketIndex,
			swapDetails.toMarketIndex
		);
		const result = await swap(toastId, swapDetails);

		setIsLoading(false);

		if (result) {
			hide();
		}
	};

	const payValue = isExactIn
		? `${exactAmountString} ${fromSpotConfig.symbol}`
		: `${worstAmount} ${fromSpotConfig.symbol}`;
	const receiveValue = isExactIn
		? `${worstAmount} ${toSpotConfig.symbol}`
		: `${exactAmountString} ${toSpotConfig.symbol}`;

	return (
		<TradeConfirmationModalWrapper
			tradeSide="buy"
			isSubmitting={isLoading}
			handleConfirmation={confirmSwap}
			handleClose={hide}
			title="Confirm swap"
			reduceOnly={false}
		>
			<div className={TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME}>
				<ValueDisplay.Default label="Pay" value={payValue} />
				<ValueDisplay.Default label="Minimum Received" value={receiveValue} />
				<ValueDisplay.Default label="Price Impact" value={priceImpactString} />
				<ValueDisplay.Default label="Est. Slippage" value={slippageString} />
			</div>
		</TradeConfirmationModalWrapper>
	);
};

export default SwapConfirmationModal;
